import { sortBy } from 'lodash';
import { BasicAnimationSet, PinComponent, PinComponentType, TextAnimationSet } from '../models';
import { isTextPinComponent } from './assertions';

/** Types of pin components that can have animations (of any kind). */
export type AnimatablePinComponent = Extract<PinComponent, { type: BasicAnimatablePinComponentType | 'TEXT' }>;

/** A pin component that has animations set (i.e. they're no longer nullable). */
export type PinComponentWithAnimations = AnimatablePinComponent & {
  settings: { animations: BasicAnimationSet | TextAnimationSet };
};

/** Types of pin components that can have basic animations. */
export type BasicAnimatablePinComponentType = (typeof basicAnimatableTypes)[number];

/** Pin components that can have basic animations. */
export type BasicAnimatablePinComponent = Extract<PinComponent, { type: BasicAnimatablePinComponentType }>;

const basicAnimatableTypes = [
  'AUDIO',
  'HOTSPOT',
  'IMAGE',
  'LINE',
  'SHAPE',
  'SPINNER',
  'TIMER',
  'UNEDITABLE_SHAPE',
  'VIDEO',
] as const;

export function supportsAnimation(pinComponent: PinComponent): pinComponent is AnimatablePinComponent {
  return isTextPinComponent(pinComponent) || supportsBasicAnimation(pinComponent);
}

/**
 * Whether a pin component can have basic animations (regardless of whether
 * they're set or not).
 */
export function supportsBasicAnimation(pinComponent: PinComponent): pinComponent is BasicAnimatablePinComponent {
  // Assign the list to a wider type to avoid type errors or having to use 'as'.
  const wider: readonly PinComponentType[] = basicAnimatableTypes;
  return wider.includes(pinComponent.type);
}

/** Type guard to check if a pin component has animations set. */
export function componentHasAnimations(pinComponent: PinComponent): pinComponent is PinComponentWithAnimations {
  if (!isTextPinComponent(pinComponent) && !supportsBasicAnimation(pinComponent)) {
    return false;
  }

  return pinComponent.settings.animations !== undefined;
}

/**
 * Returns all pin components with animations, sorted by animation order and component id.
 *
 * @param pinComponents - Array of pin components to filter and sort
 * @returns Array of components with animations, sorted by order then id
 */
export function filterAnimatedComponents(pinComponents: PinComponent[]): PinComponentWithAnimations[] {
  const componentsWithAnimations = pinComponents.filter(componentHasAnimations);
  return sortBy(componentsWithAnimations, (component) => [component.settings.animations.entry.order, component.id]);
}
